import type { IRowNode } from 'ag-grid-community';
import { useCallback, useRef } from 'react';

type FieldSelector<TData> = (row: TData) => string | null | undefined;

type UseEngagementFlowClientFilterOptions<TData> = {
  fields: FieldSelector<TData>[];
  onSearchChange?: (searchValue: string) => void;
  onFilterChanged?: () => void;
};

export function useEngagementFlowClientFilter<TData>({
  fields,
  onSearchChange,
  onFilterChanged,
}: UseEngagementFlowClientFilterOptions<TData>) {
  const searchRef = useRef('');

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      searchRef.current = searchValue;
      onSearchChange?.(searchValue);
      onFilterChanged?.();
    },
    [onFilterChanged, onSearchChange],
  );

  const isExternalFilterPresent = useCallback(() => searchRef.current.trim().length > 0, []);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<TData>) => {
      const data = node.data;
      if (!data) {
        return false;
      }

      const query = searchRef.current.trim().toLowerCase();
      if (!query) {
        return true;
      }

      return fields.some((selector) => (selector(data) ?? '').toLowerCase().includes(query));
    },
    [fields],
  );

  return {
    handleSearchChange,
    isExternalFilterPresent,
    doesExternalFilterPass,
  };
}
